"use client"

import { addToCart } from "@lib/data/cart"
import { useIntersection } from "@lib/hooks/use-in-view"
import { HttpTypes } from "@medusajs/types"
import AmzPrice from "@modules/amz/components/price"
import { deliveryEstimate } from "@modules/amz/components/delivery"
import ExpressCheckout from "@modules/checkout/components/express-checkout"
import OptionSelect from "@modules/products/components/product-actions/option-select"
import { isEqual } from "lodash"
import { useParams, usePathname, useSearchParams } from "next/navigation"
import { useEffect, useMemo, useRef, useState } from "react"
import MobileActions from "./mobile-actions"
import { useRouter } from "next/navigation"

type ProductActionsProps = {
  product: HttpTypes.StoreProduct
  region: HttpTypes.StoreRegion
  disabled?: boolean
  /** Title and rating block rendered above the price */
  info?: React.ReactNode
  /** Logged-in customers pay "Comprar ya" with their saved details */
  loggedIn?: boolean
  /** Order amount from which shipping is free (null: never free) */
  freeShippingFrom?: number | null
}

type PricedVariant = HttpTypes.StoreProductVariant & {
  calculated_price?: { calculated_amount?: number; currency_code?: string } | null
}

const optionsAsKeymap = (
  variantOptions: HttpTypes.StoreProductVariant["options"]
) => {
  return variantOptions?.reduce((acc: Record<string, string>, varopt) => {
    if (varopt.option_id) acc[varopt.option_id] = varopt.value
    return acc
  }, {})
}

export default function ProductActions({
  product,
  region,
  disabled,
  info,
  loggedIn,
  freeShippingFrom,
}: ProductActionsProps) {
  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()

  const [options, setOptions] = useState<Record<string, string | undefined>>({})
  const [isAdding, setIsAdding] = useState(false)
  const [quantity, setQuantity] = useState(1)
  const countryCode = useParams().countryCode as string

  // If there is only 1 variant, preselect the options
  useEffect(() => {
    if (product.variants?.length === 1) {
      const variantOptions = optionsAsKeymap(product.variants[0].options)
      setOptions(variantOptions ?? {})
    }
  }, [product.variants])

  const selectedVariant = useMemo(() => {
    if (!product.variants || product.variants.length === 0) {
      return
    }

    return product.variants.find((v) => {
      const variantOptions = optionsAsKeymap(v.options)
      return isEqual(variantOptions, options)
    })
  }, [product.variants, options])

  // update the options when a variant is selected
  const setOptionValue = (optionId: string, value: string) => {
    setOptions((prev) => ({
      ...prev,
      [optionId]: value,
    }))
  }

  //check if the selected options produce a valid variant
  const isValidVariant = useMemo(() => {
    return product.variants?.some((v) => {
      const variantOptions = optionsAsKeymap(v.options)
      return isEqual(variantOptions, options)
    })
  }, [product.variants, options])

  useEffect(() => {
    const params = new URLSearchParams(searchParams.toString())
    const value = isValidVariant ? selectedVariant?.id : null

    if (params.get("v_id") === value) {
      return
    }

    if (value) {
      params.set("v_id", value)
    } else {
      params.delete("v_id")
    }

    router.replace(pathname + "?" + params.toString())
  }, [selectedVariant, isValidVariant])

  // check if the selected variant is in stock
  const inStock = useMemo(() => {
    // If we don't manage inventory, we can always add to cart
    if (selectedVariant && !selectedVariant.manage_inventory) {
      return true
    }

    // If we allow back orders on the variant, we can add to cart
    if (selectedVariant?.allow_backorder) {
      return true
    }

    // If there is inventory available, we can add to cart
    if (
      selectedVariant?.manage_inventory &&
      (selectedVariant?.inventory_quantity || 0) > 0
    ) {
      return true
    }

    // Otherwise, we can't add to cart
    return false
  }, [selectedVariant])

  const actionsRef = useRef<HTMLDivElement>(null)

  const inView = useIntersection(actionsRef, "0px")

  // add the selected variant to the cart
  const handleAddToCart = async () => {
    if (!selectedVariant?.id) return null

    setIsAdding(true)

    await addToCart({
      variantId: selectedVariant.id,
      quantity,
      countryCode,
    })

    setIsAdding(false)
  }

  // Values of each option actually used by this product's variants.
  const availableValues = useMemo(() => {
    const map: Record<string, string[]> = {}
    for (const v of product.variants ?? []) {
      for (const o of v.options ?? []) {
        if (!o.option_id) continue
        if (!(map[o.option_id] ??= []).includes(o.value)) map[o.option_id].push(o.value)
      }
    }
    return map
  }, [product.variants])

  const priced = (selectedVariant ??
    [...((product.variants ?? []) as PricedVariant[])].sort(
      (a, b) =>
        (a.calculated_price?.calculated_amount ?? 0) -
        (b.calculated_price?.calculated_amount ?? 0)
    )[0]) as PricedVariant | undefined
  const amount = priced?.calculated_price?.calculated_amount
  const currencyCode = priced?.calculated_price?.currency_code ?? region.currency_code
  const compareAt = Number((priced?.metadata as Record<string, unknown> | null)?.compare_at_price ?? 0)
  const discount = amount && compareAt > amount ? Math.round((1 - amount / compareAt) * 100) : null
  const stock = selectedVariant?.inventory_quantity ?? null
  const delivery = deliveryEstimate(2, 4)
  const fastDelivery = deliveryEstimate(1, 1)
  const buttonLabel = !selectedVariant
    ? "Selecciona una opción"
    : !inStock || !isValidVariant
    ? "Agotado"
    : "Añadir a la cesta"
  const buttonsDisabled =
    !inStock || !selectedVariant || !!disabled || isAdding || !isValidVariant

  const priceBlock =
    amount !== undefined && amount !== null ? (
      <div className="flex flex-col gap-y-2">
        <div className="flex items-center gap-x-3">
          <span data-testid="product-price" data-value={amount}>
            <AmzPrice amount={amount} currencyCode={currencyCode} size="lg" />
          </span>
          {discount && (
            <span className="zl-badge bg-zl-red text-white px-1.5 py-1">-{discount}%</span>
          )}
        </div>
        <span className="zl-badge text-zl-steel">
          {discount && (
            <>
              PVPR{" "}
              <span className="line-through" data-testid="original-product-price">
                {compareAt.toLocaleString("es-ES", { style: "currency", currency: currencyCode.toUpperCase() })}
              </span>{" "}
              {"// "}
            </>
          )}
          IVA incluido
        </span>
      </div>
    ) : (
      <div className="block w-32 h-9 bg-zl-surface animate-pulse" />
    )

  const stockLabel = !selectedVariant
    ? null
    : !inStock
    ? "Agotado"
    : stock !== null && selectedVariant.manage_inventory && stock < 10
    ? `Últimas ${stock} uds.`
    : "En stock"

  return (
    <div className="flex flex-col gap-y-8" ref={actionsRef}>
      {info}
      <div className="flex items-end justify-between gap-x-4 border-t border-zl-line pt-6">
        {priceBlock}
        {stockLabel && (
          <span className="zl-chip shrink-0" data-testid="stock-status">
            <span className={inStock ? "text-zl-green" : "text-zl-red"}>{inStock ? "\u25CF" : "\u25CB"}</span>
            {stockLabel}
          </span>
        )}
      </div>

      {(product.variants?.length ?? 0) > 1 && (
        <div className="flex flex-col gap-y-6">
          {(product.options || []).map((option) => (
            <OptionSelect
              key={option.id}
              option={option}
              current={options[option.id]}
              updateOption={setOptionValue}
              title={option.title ?? ""}
              availableValues={availableValues[option.id]}
              data-testid="product-options"
              disabled={!!disabled || isAdding}
            />
          ))}
        </div>
      )}

      <div className="flex flex-col gap-y-3">
        <div className="flex gap-x-3">
          <div className="flex items-center border border-zl-ink h-11 shadow-inset" role="group" aria-label="Cantidad">
            <button
              type="button"
              onClick={() => setQuantity((q) => Math.max(1, q - 1))}
              className="w-10 h-full font-mono hover:text-zl-red disabled:opacity-40"
              disabled={quantity <= 1}
              aria-label="Menos"
            >
              &minus;
            </button>
            <span className="w-8 text-center font-mono text-sm tabular-nums" data-testid="product-quantity" data-value={quantity}>
              {quantity}
            </span>
            <button
              type="button"
              onClick={() => setQuantity((q) => Math.min(10, q + 1))}
              className="w-10 h-full font-mono hover:text-zl-red disabled:opacity-40"
              disabled={quantity >= 10}
              aria-label="Más"
            >
              +
            </button>
          </div>
          <button
            onClick={handleAddToCart}
            disabled={buttonsDisabled}
            className="zl-btn flex-1"
            data-testid="add-product-button"
          >
            {isAdding ? "Añadiendo..." : buttonLabel}
          </button>
        </div>
        <ExpressCheckout
          source={
            selectedVariant?.id && isValidVariant
              ? { type: "product", variantId: selectedVariant.id, quantity }
              : null
          }
          amount={Math.round((amount ?? 0) * quantity * 100)}
          currency={currencyCode}
          disabled={buttonsDisabled}
          renderBuyNow={(open, buyDisabled, redirecting) => (
            <button
              onClick={open}
              disabled={buyDisabled}
              className="zl-btn-outline w-full"
              data-testid="buy-now-button"
            >
              {redirecting ? "Redirigiendo..." : "Comprar ya"}
            </button>
          )}
        />
        <p className="text-xs text-zl-steel">
          {loggedIn
            ? "Compra en un paso con tu dirección y tu método de pago habituales."
            : "Compra en una sola página, sin crear cuenta: Apple Pay, Google Pay, Bizum o tarjeta."}
        </p>
      </div>

      <dl className="border-t border-zl-line">
        {[
          [
            "Entrega",
            amount && freeShippingFrom != null && amount >= freeShippingFrom
              ? `Gratis // ${delivery.from}`
              : `${delivery.from} - ${delivery.to}`,
          ],
          ["Express", fastDelivery.from],
          ...(freeShippingFrom != null
            ? [["Envío", `Gratis desde ${freeShippingFrom} EUR`]]
            : []),
          ["Pago", "100 % seguro"],
          ["Devolución", "30 días"],
        ].map(([k, v]) => (
          <div key={k} className="grid grid-cols-[112px_1fr] gap-x-4 py-2.5 border-b border-zl-line">
            <dt className="zl-badge text-zl-steel pt-0.5">{k}</dt>
            <dd className="text-sm first-letter:uppercase">{v}</dd>
          </div>
        ))}
      </dl>
      <MobileActions
        product={product}
        variant={selectedVariant}
        options={options}
        updateOptions={setOptionValue}
        inStock={inStock}
        handleAddToCart={handleAddToCart}
        isAdding={isAdding}
        show={!inView}
        optionsDisabled={!!disabled || isAdding}
      />
    </div>
  )
}
